import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import api from '../api/axios';
import { usePermissions } from '../context/PermissionContext';

interface Client { id: number; company_name?: string; contact_person?: string; }
interface UserShort { id: number; full_name: string; role: string; }
interface Instrument {
  id: number;
  inventory_number: string;
  model: string;
  manufacturer?: string;
  serial_number?: string;
  installation_date?: string;
  warranty_end?: string;
  status: string;
  notes?: string;
  client_id?: number;
  client?: { id: number; company_name?: string; contact_person?: string; };
  responsible_engineer_id?: number;
  responsible_engineer?: { id: number; full_name: string; };
  creator?: { id: number; full_name: string; };
  created_at: string;
}

const statusConfig: Record<string, { label: string; color: string; icon: string }> = {
  active:      { label: 'Faol',                        color: 'bg-green-100 text-green-700 border-green-300',   icon: '✅' },
  maintenance: { label: 'Texnik xizmatda',             color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: '🔧' },
  retired:     { label: 'Foydalanishdan chiqarilgan',  color: 'bg-red-100 text-red-700 border-red-300',         icon: '❌' },
};

const emptyForm = {
  inventory_number: '', model: '', manufacturer: '', serial_number: '',
  installation_date: '', warranty_end: '', status: 'active',
  notes: '', client_id: '', responsible_engineer_id: '',
};

const ServiceModule: React.FC = () => {
  const { canEdit } = usePermissions();
  const canFull = canEdit('service_module');
  const location = useLocation();

  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [engineers, setEngineers] = useState<UserShort[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [showDetail, setShowDetail] = useState<Instrument | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const params: any = {};
    if (search) params.search = search;
    if (filterStatus) params.status = filterStatus;
    try {
      const instRes = await api.get('/instruments', { params });
      setInstruments(instRes.data);
    } catch (e) { console.error(e); }
    try {
      const clientRes = await api.get('/clients');
      setClients(clientRes.data);
    } catch (e) { console.error(e); }
    try {
      const userRes = await api.get('/users/assignable');
      setEngineers(userRes.data.filter((u: UserShort) => ['service_engineer', 'service_head', 'admin'].includes(u.role)));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [search, filterStatus]);

  const openAdd = () => { setForm(emptyForm); setEditId(null); setError(''); setShowModal(true); };
  const openEdit = (inst: Instrument) => {
    setForm({
      inventory_number: inst.inventory_number,
      model: inst.model,
      manufacturer: inst.manufacturer || '',
      serial_number: inst.serial_number || '',
      installation_date: inst.installation_date?.split('T')[0] || '',
      warranty_end: inst.warranty_end?.split('T')[0] || '',
      status: inst.status,
      notes: inst.notes || '',
      client_id: inst.client_id?.toString() || '',
      responsible_engineer_id: inst.responsible_engineer_id?.toString() || '',
    });
    setEditId(inst.id);
    setShowDetail(null);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.inventory_number || !form.model) { setError("Inventar raqami va model majburiy!"); return; }
    setLoading(true); setError('');
    try {
      const payload: any = {
        ...form,
        client_id: form.client_id ? Number(form.client_id) : null,
        responsible_engineer_id: form.responsible_engineer_id ? Number(form.responsible_engineer_id) : null,
        installation_date: form.installation_date || null,
        warranty_end: form.warranty_end || null,
        manufacturer: form.manufacturer || null,
        serial_number: form.serial_number || null,
        notes: form.notes || null,
      };
      if (editId) await api.put(`/instruments/${editId}`, payload);
      else await api.post('/instruments', payload);
      setShowModal(false);
      fetchData();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Xatolik yuz berdi');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("O'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/instruments/${id}`);
    fetchData();
    setShowDetail(null);
  };

  const handleExportOne = async (id: number, invNum: string) => {
    try {
      const res = await api.get(`/instruments/${id}/export/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `instrument_${invNum}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('PDF yaratishda xato'); }
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const params: any = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/instruments/export/pdf', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'instruments_royxat.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("PDF yaratishda xato"); }
    finally { setExporting(false); }
  };

  const getClientName = (inst: Instrument) =>
    inst.client?.company_name || inst.client?.contact_person || '—';

  const isWarrantyExpiring = (warrantyEnd?: string) => {
    if (!warrantyEnd) return false;
    const end = new Date(warrantyEnd);
    const in90 = new Date(); in90.setDate(in90.getDate() + 90);
    return end <= in90 && end >= new Date();
  };

  const isWarrantyExpired = (warrantyEnd?: string) => {
    if (!warrantyEnd) return false;
    return new Date(warrantyEnd) < new Date();
  };

  // Dashboard "Kafolati tugayotgan asboblar (30 kun)" kartasi ?warranty=expiring
  // bilan keladi - backend/app/routers/dashboard.py'dagi _warranty_expiring()
  // bilan BIR XIL 30 KUNLIK oyna (yuqoridagi isWarrantyExpiring() esa bu
  // sahifaning o'z ⏰ belgisi uchun 90 kunlik oyna ishlatadi - ikkisi ATAYLAB
  // boshqacha, shuning uchun karta soni bilan mos kelishi uchun bu yerda
  // ALOHIDA 30-kunlik tekshiruv ishlatiladi).
  const isWarrantyExpiringWithin30 = (warrantyEnd?: string) => {
    if (!warrantyEnd) return false;
    const end = new Date(warrantyEnd);
    const in30 = new Date(); in30.setDate(in30.getDate() + 30);
    return end <= in30 && end >= new Date();
  };
  const warrantyFilter = new URLSearchParams(location.search).get('warranty') === 'expiring';
  const visibleInstruments = warrantyFilter
    ? instruments.filter(i => i.status === 'active' && isWarrantyExpiringWithin30(i.warranty_end))
    : instruments;

  return (
    <div className="p-3 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-4 md:mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">🔬 Servis & Instrument Registry</h2>
          <p className="text-gray-500 text-xs md:text-sm mt-1">Laboratoriya uskunalarini boshqarish</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleExportAll} disabled={exporting}
            className="px-3 md:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50">
            {exporting ? '⏳ Yuklanmoqda...' : "📄 Ro'yxat PDF"}
          </button>
          {canFull && (
            <button onClick={openAdd}
              className="px-3 md:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
              + <span className="hidden sm:inline">Yangi instrument</span>
            </button>
          )}
        </div>
      </div>

      {warrantyFilter && (
        <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3 text-xs md:text-sm text-blue-700">
          <span>🔎 Kafolati 30 kun ichida tugaydigan asboblar ko'rsatilmoqda</span>
          <Link to="/service-module" className="underline hover:no-underline whitespace-nowrap">Filtrni tozalash ✕</Link>
        </div>
      )}

      {/* Filtr */}
      <div className="flex flex-col sm:flex-row gap-2 md:gap-3 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Inventar, model, serial raqam..."
          className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Barcha holatlar</option>
          {Object.entries(statusConfig).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>
      </div>

      {/* Statistika */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-3 mb-4">
        {Object.entries(statusConfig).map(([k, v]) => (
          <div key={k} className={`rounded-xl p-3 border ${v.color} flex items-center gap-2`}>
            <span className="text-xl">{v.icon}</span>
            <div>
              <p className="text-xs font-medium">{v.label}</p>
              <p className="text-lg font-bold">{instruments.filter(i => i.status === k).length}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Jadval */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-blue-900 text-white text-xs">
              <th className="px-3 md:px-4 py-3 text-left">INVENTAR</th>
              <th className="px-3 md:px-4 py-3 text-left">MODEL</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">ISHLAB CHIQARUVCHI</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">SERIAL</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">MIJOZ</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">INJENER</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">KAFOLAT</th>
              <th className="px-3 md:px-4 py-3 text-left">HOLAT</th>
              <th className="px-3 md:px-4 py-3 text-left">AMALLAR</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-10 text-gray-400">Yuklanmoqda...</td></tr>
            ) : visibleInstruments.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-10 text-gray-400">
                {warrantyFilter ? "Kafolati 30 kun ichida tugaydigan asbob yo'q" : "Instrumentlar yo'q"}
              </td></tr>
            ) : visibleInstruments.map((inst, idx) => {
              const cfg = statusConfig[inst.status] || statusConfig.active;
              const expiring = isWarrantyExpiring(inst.warranty_end);
              const expired = isWarrantyExpired(inst.warranty_end);
              return (
                <tr key={inst.id} className={`border-b hover:bg-gray-50 cursor-pointer ${idx % 2 === 0 ? '' : 'bg-gray-50/50'}`}
                  onClick={() => setShowDetail(inst)}>
                  <td className="px-3 md:px-4 py-3">
                    <span className="font-mono font-bold text-blue-600 text-xs md:text-sm">{inst.inventory_number}</span>
                  </td>
                  <td className="px-3 md:px-4 py-3">
                    <div className="font-medium text-gray-800 text-sm">{inst.model}</div>
                    {/* Mobil: yashiringan ustunlarning qisqacha ko'rinishi */}
                    <div className="md:hidden text-xs text-gray-400 mt-0.5 truncate">
                      {inst.manufacturer && <>{inst.manufacturer} · </>}{getClientName(inst)}
                    </div>
                    {inst.warranty_end && (
                      <div className="md:hidden text-xs mt-0.5">
                        <span className={expired ? 'text-red-600' : expiring ? 'text-yellow-600' : 'text-green-600'}>
                          {expired ? '⚠️' : expiring ? '⏰' : '✅'} {new Date(inst.warranty_end).toLocaleDateString('ru-RU')}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 md:px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{inst.manufacturer || '—'}</td>
                  <td className="px-3 md:px-4 py-3 text-sm text-gray-500 font-mono hidden md:table-cell">{inst.serial_number || '—'}</td>
                  <td className="px-3 md:px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{getClientName(inst)}</td>
                  <td className="px-3 md:px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{inst.responsible_engineer?.full_name || '—'}</td>
                  <td className="px-3 md:px-4 py-3 text-sm hidden md:table-cell">
                    {inst.warranty_end ? (
                      <span className={`font-medium ${expired ? 'text-red-600' : expiring ? 'text-yellow-600' : 'text-green-600'}`}>
                        {expired ? '⚠️ ' : expiring ? '⏰ ' : '✅ '}
                        {new Date(inst.warranty_end).toLocaleDateString('ru-RU')}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 md:px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
                      {cfg.icon} {cfg.label}
                    </span>
                  </td>
                  <td className="px-3 md:px-4 py-3">
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => handleExportOne(inst.id, inst.inventory_number)}
                        title="PDF kartochka"
                        className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 text-xs">📄</button>
                      {canFull && (
                        <>
                          <button onClick={() => openEdit(inst)}
                            className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 text-xs">✏️</button>
                          <button onClick={() => handleDelete(inst.id)}
                            className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 text-xs">🗑️</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className="font-mono font-bold text-blue-600 text-base md:text-lg">{showDetail.inventory_number}</span>
                <h3 className="text-lg md:text-xl font-bold text-gray-800">{showDetail.model}</h3>
                {showDetail.manufacturer && <p className="text-sm text-gray-500">{showDetail.manufacturer}</p>}
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusConfig[showDetail.status]?.color}`}>
                {statusConfig[showDetail.status]?.icon} {statusConfig[showDetail.status]?.label}
              </span>
            </div>

            <div className="space-y-2 text-sm mb-4">
              {[
                ['📋 Serial raqam', showDetail.serial_number],
                ['📅 O\'rnatilgan sana', showDetail.installation_date ? new Date(showDetail.installation_date).toLocaleDateString('ru-RU') : null],
                ['🛡️ Kafolat muddati', showDetail.warranty_end ? new Date(showDetail.warranty_end).toLocaleDateString('ru-RU') : null],
                ['🏢 Mijoz', showDetail.client?.company_name || showDetail.client?.contact_person],
                ['👷 Mas\'ul injener', showDetail.responsible_engineer?.full_name],
                ['✍️ Yaratuvchi', showDetail.creator?.full_name],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label as string} className="flex gap-2 flex-wrap">
                  <span className="text-gray-500 min-w-40">{label as string}:</span>
                  <span className="font-medium text-gray-800">{value as string}</span>
                </div>
              ))}
              {showDetail.notes && (
                <div className="bg-gray-50 rounded-lg p-3 mt-2">
                  <p className="text-xs text-gray-500 mb-1">Izoh:</p>
                  <p className="text-gray-700">{showDetail.notes}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t flex-wrap">
              <button onClick={() => handleExportOne(showDetail.id, showDetail.inventory_number)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                📄 PDF kartochka
              </button>
              {canFull && (
                <button onClick={() => openEdit(showDetail)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                  ✏️ Tahrirlash
                </button>
              )}
              <button onClick={() => setShowDetail(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
            </div>
          </div>
        </div>
      )}

      {/* Yaratish/Tahrirlash Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editId ? 'Instrumentni tahrirlash' : 'Yangi instrument'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {[
                { label: 'Inventar raqami *', key: 'inventory_number', placeholder: 'HPLC-001' },
                { label: 'Model *', key: 'model', placeholder: 'Bikai HPLC' },
                { label: 'Ishlab chiqaruvchi', key: 'manufacturer', placeholder: 'Shimadzu' },
                { label: 'Serial raqam', key: 'serial_number', placeholder: 'SN-123456' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                  <input type="text" value={(form as any)[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">O'rnatilgan sana</label>
                <input type="date" value={form.installation_date}
                  onChange={e => setForm({ ...form, installation_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kafolat muddati</label>
                <input type="date" value={form.warranty_end}
                  onChange={e => setForm({ ...form, warranty_end: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mijoz</label>
                <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.company_name || c.contact_person}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mas'ul injener</label>
                <select value={form.responsible_engineer_id}
                  onChange={e => setForm({ ...form, responsible_engineer_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {engineers.map(u => (
                    <option key={u.id} value={u.id}>{u.full_name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Holat</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {Object.entries(statusConfig).map(([k, v]) => (
                    <option key={k} value={k}>{v.icon} {v.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows={3} placeholder="Qo'shimcha ma'lumotlar..."
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor qilish</button>
              <button onClick={handleSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceModule;
